import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import {
  ModalInner,
  ModalContentsGrid,
  ModalHeader,
} from '@components/wrapper/Modal/ModalStyle';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { replaceContents } from '@utils/replaceContents';
import { useMemberPermission } from '@hooks/usePermission';
import SelectMyCharacter from '@components/dataEntry/select/SelectUsers/SelectMyCharacter';
import { useRecoilValue } from 'recoil';
import { currentUserIDState, currentUserWalletState } from '@atom/setting/user';
import WalletPrice from '@pages/Store/component/WalletPrice';
import ParchaseConfirmModal from '@pages/Store/Detail/ParchaseConfirmModal';
import {
  IluckydrawItem,
  LUCKYDRAW_OPEN_MODE,
} from '@typings/store/luckydraw.interface';
import ItemMaterialSec from '@components/dataDisplay/slider/ItemMaterialSec';
import CompareMyInventory from './CompareMyInventory';
import { getItemData, parchaseItem } from '@store/firebase/store/parchase-item';
import { CollectionPath } from '@typings/collections.enum';
import { IstoreItem } from '@typings/store/storeItem.interface';
import RewardItemsSec from './RewardItemsSec';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import { selectLuckyDrawReward } from '@utils/luckydraw/selectLuckyDrawReward';
import { presentSkill } from '@store/firebase/skill/update-skill';
import { fuego } from '@nandorojo/swr-firestore';
import { normalizeRewardResellPrice } from '@utils/luckydraw/normalizeRewardResellPrice';

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const TitleBannerSec = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  img {
    height: 200px;
    object-fit: contain;
  }
`;

const ContentsSec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0;

  h6 {
    font-size: 18px;
  }
`;

type Props = {
  data: IluckydrawItem;
  closeModal: () => void;
};
function LuckyDrawModal({ data, closeModal }: Props) {
  const { title, contents, imgURL, price, materialCount } = data || {};
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const closeConfirmModal = () => {
    setOpenConfirm(false);
  };
  const currentUserID = useRecoilValue(currentUserIDState);
  const controlPermission = useMemberPermission({});
  const [disabled, setDisabled] = useState(false);
  const [randomItem, setRandomItem] = useState<IstoreItem>();
  const currentUserWallet = useRecoilValue(currentUserWalletState);
  const wallet = Number(currentUserWallet || 0);
  const drawCost = Number(price || 0);
  const rewardResellPrice = normalizeRewardResellPrice(
    data.rewardResellPrice,
  );
  const isWalletInsufficient = wallet < drawCost;
  const blockBtn = disabled || !selectedOwnerId || isWalletInsufficient;

  const handleClick = useCallback(async () => {
    if (
      !selectedOwnerId ||
      Number(currentUserWallet || 0) < Number(price || 0)
    ) {
      alert('구매할 수 없습니다. 소지금/선택 상태를 확인하세요.');
      return;
    }

    const openMode = data.openMode || LUCKYDRAW_OPEN_MODE.INSTANT;

    if (openMode === LUCKYDRAW_OPEN_MODE.INSTANT) {
      // 모드 1: 즉시 오픈 (기존 로직)
      const selectedReward = selectLuckyDrawReward(data);
      if (!selectedReward) return;

      const pickItem = selectedReward.id;
      const isSkillItem = selectedReward.type === 'skill';

      let itemData = await getItemData({ itemID: pickItem });

      if (!itemData && isSkillItem) {
        itemData = await fuego.db
          .collection(CollectionPath.SKILLS)
          .doc(pickItem)
          .get()
          .then((doc) => (doc.exists ? doc.data() : null));
      }

      setRandomItem(itemData);

      if (!itemData) return;
      if (isSkillItem) {
        await presentSkill({
          collectionPath: CollectionPath.PHOTOCARD,
          docId: selectedOwnerId,
          skillIDList: [pickItem],
        });
      } else {
        await parchaseItem({
          collectionPath: `${CollectionPath.PHOTOCARD}`,
          photocardID: selectedOwnerId,
          data: {
            ...itemData,
            price: data.price,
            id: pickItem,
            isResellable: true,
            ...(rewardResellPrice !== undefined
              ? { resellPrice: rewardResellPrice }
              : {}),
            acquisitionSource: 'luckyDraw',
          },
          amount: 1,
          buyerID: currentUserID || '',
          materialCount: data.materialCount || 0,
          materialItemID: data.materialItemID || null,
        });
      }

      setOpenConfirm(true);
    } else if (
      openMode === LUCKYDRAW_OPEN_MODE.USE_ITEM ||
      openMode === LUCKYDRAW_OPEN_MODE.POST_UPLOAD
    ) {
      // 모드 2 & 3: 랜덤박스 아이템 지급
      const randomBoxId = `randombox-${data.id}`;

      // IstoreItem 형식에 맞춰 랜덤박스 아이템 생성
      const randomBoxItem: IstoreItem = {
        id: randomBoxId,
        title: `${data.title}`,
        description:
          openMode === LUCKYDRAW_OPEN_MODE.USE_ITEM
            ? `${data.title} 입니다. [뽑기 오픈]을 통해 랜덤 아이템을 획득할 수 있습니다.`
            : `${data.title} 입니다. 게시글 업로드 시 아이템을 오픈할 수 있습니다.`,
        imgURL: data.randomBoxImgURL || data.imgURL || '',
        price: String(data.price || 0),
        order: 0,
        isShow: true,
        isGiftable: true,
        isResellable: true,
        acquisitionSource: 'luckyDraw',
        functionType:
          openMode === LUCKYDRAW_OPEN_MODE.POST_UPLOAD
            ? ITEM_FUNCTION_TPYE.RANDOM_BOX_POST
            : ITEM_FUNCTION_TPYE.RANDOM_BOX,
        randomBoxData: {
          luckydrawId: data.id,
          itemList: data.itemList,
          skillList: data.skillList,
          rewardChanceList: data.rewardChanceList,
          rewardResellPrice: rewardResellPrice ?? null,
          openMode: openMode, // 오픈 모드 저장
        },
        createdAt: new Date(),
      };

      // 랜덤박스를 인벤토리에 지급
      await parchaseItem({
        collectionPath: `${CollectionPath.PHOTOCARD}`,
        photocardID: selectedOwnerId,
        data: randomBoxItem,
        amount: 1,
        buyerID: currentUserID || '',
        materialCount: data.materialCount || 0,
        materialItemID: data.materialItemID || null,
      });

      // 확인 모달 표시
      setRandomItem(randomBoxItem);
      setOpenConfirm(true);
    }
  }, [
    data,
    selectedOwnerId,
    currentUserID,
    currentUserWallet,
    price,
    rewardResellPrice,
    closeModal,
  ]);

  return (
    <>
      <ModalWrapper
        outsideClick={closeModal}
        contentStyle={{ minWidth: '500px' }}
      >
        <ModalContentsGrid>
          <ModalHeader>
            <ButtonWrap>
              <CloseBtn onClick={closeModal} />
              <h6>{title}</h6>
            </ButtonWrap>
            <div />
            {controlPermission && (
              <ButtonWrap>
                <SelectMyCharacter
                  label="캐릭터"
                  selectedOwnerId={selectedOwnerId}
                  setSelectedOwnerId={setSelectedOwnerId}
                />
              </ButtonWrap>
            )}
          </ModalHeader>
          <ModalInner>
            {data.imgURL && (
              <TitleBannerSec>
                <img src={data.imgURL} alt="banner" />
              </TitleBannerSec>
            )}
            <ContentsSec>
              {contents && (
                <>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: replaceContents(contents),
                    }}
                  />
                </>
              )}
            </ContentsSec>
            <RewardItemsSec data={data} />
            {/* 재료가 설정되지 않으면 표시될 필요 없음 */}
            {data.materialItemID && (
              <CompareMyInventory
                materialItemID={data.materialItemID || ''}
                photocardID={selectedOwnerId}
                setDisabled={setDisabled}
                materialCount={data.materialCount}
              />
            )}
            <ItemMaterialSec
              data={data}
              handleClick={handleClick}
              disabled={blockBtn}
              size={'md'}
              style={{ width: '100%' }}
              showMaterial={false}
              showMyWallet={true}
            />
          </ModalInner>
        </ModalContentsGrid>
      </ModalWrapper>

      {openConfirm && (
        <ParchaseConfirmModal
          closeModal={closeConfirmModal}
          data={randomItem}
        />
      )}
    </>
  );
}

export default LuckyDrawModal;
